import { useState, type ReactNode } from 'react';
import { LogOut, Menu, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle } from '@london-life/ui/components/sheet';
import { NavMenu } from './nav-menu';
import type { Can, NavGroup, NavLinkComponent } from './types';

type Props = {
  title: string;
  environment: string;
  user: { name: string; detail: string };
  groups: NavGroup[];
  pathname: string;
  can: Can;
  Link: NavLinkComponent;
  breadcrumbs?: ReactNode;
  onSignOut: () => void;
  children: ReactNode;
};

const COLLAPSE_KEY = 'll_admin_menu_collapsed';
const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
};

// the admin frame: top bar across the page, the menu in 3 of 12 columns, the content in the other 9;
// the menu can shrink to icons, and on small screens it becomes a drawer
export function AdminShell({
  title,
  environment,
  user,
  groups,
  pathname,
  can,
  Link,
  breadcrumbs,
  onSignOut,
  children,
}: Props) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawer, setDrawer] = useState(false);
  const toggle = () =>
    setCollapsed((current) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, current ? '0' : '1');
      } catch {
        /* storage unavailable: the choice lasts for this visit only */
      }
      return !current;
    });
  const account = (
    <div className="admin-account">
      <span className="admin-account-name">{user.name}</span>
      <span className="admin-account-detail">{user.detail}</span>
    </div>
  );

  return (
    <div className={`admin-frame${collapsed ? ' is-collapsed' : ''}`}>
      <header className="admin-topbar">
        <button
          type="button"
          className="admin-icon-button admin-drawer-button"
          aria-label="Open menu"
          onClick={() => setDrawer(true)}
        >
          <Menu aria-hidden />
        </button>
        <strong className="admin-brand">{title}</strong>
        <span className="admin-env" data-env={environment.toLowerCase()}>
          {environment}
        </span>
        <div className="admin-breadcrumbs">{breadcrumbs}</div>
        <div className="admin-topbar-account">{account}</div>
        <button type="button" className="admin-icon-button" aria-label="Sign out" title="Sign out" onClick={onSignOut}>
          <LogOut aria-hidden />
        </button>
      </header>

      <aside className="admin-sidebar">
        <NavMenu groups={groups} pathname={pathname} can={can} Link={Link} collapsed={collapsed} />
        <button
          type="button"
          className="admin-collapse"
          onClick={toggle}
          aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
          title={collapsed ? 'Expand menu' : 'Collapse menu'}
        >
          {collapsed ? <PanelLeftOpen aria-hidden /> : <PanelLeftClose aria-hidden />}
          {!collapsed && <span>Collapse menu</span>}
        </button>
      </aside>

      <Sheet open={drawer} onOpenChange={setDrawer}>
        <SheetContent side="left" className="admin-drawer">
          <SheetTitle className="admin-drawer-title">{title}</SheetTitle>
          {account}
          <NavMenu groups={groups} pathname={pathname} can={can} Link={Link} onNavigate={() => setDrawer(false)} />
        </SheetContent>
      </Sheet>

      <main className="admin-content">{children}</main>
    </div>
  );
}
